import { useCallback, useEffect, useRef, useState } from 'react';
import {
  copilotApi, toProposal, toToolEvent, type CopilotConversation, type CopilotErrorEvent, type CopilotProposal,
  type CopilotSource, type CopilotToolEvent,
} from './copilotApi';

export type CopilotMessageStatus = 'streaming' | 'done' | 'stopped' | 'error';

export interface CopilotChatMessage {
  localId: string;
  role: 'user' | 'assistant';
  content: string;
  tools: CopilotToolEvent[];
  sources: CopilotSource[];
  proposals: CopilotProposal[];
  status: CopilotMessageStatus;
  error?: CopilotErrorEvent;
  /** Server id of an answer, for feedback. */
  messageId?: string;
  feedback?: 'UP' | 'DOWN' | null;
  /** On an answer: the question it answers, so "Try again" can ask it again. */
  question?: string;
  /** Loaded from a stored conversation (its turn is long finished; cards show their stored status). */
  fromHistory?: boolean;
}

let seq = 0;
const localId = (p: string) => `${p}-${Date.now().toString(36)}-${(seq++).toString(36)}`;

export interface SendOptions {
  patientId?: string | null;
  locale?: string;
}

/** One Copilot conversation on screen: messages, the stream in flight, and the conversation id. */
export function useCopilotChat() {
  const [messages, setMessages] = useState<CopilotChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [namesHidden, setNamesHidden] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const conversationRef = useRef<string | null>(null);
  conversationRef.current = conversationId;

  // Leaving the screen ends the stream (the server still stores the answer).
  useEffect(() => () => abortRef.current?.abort(), []);

  const patch = useCallback((id: string, fn: (m: CopilotChatMessage) => CopilotChatMessage) => {
    setMessages((prev) => prev.map((m) => (m.localId === id ? fn(m) : m)));
  }, []);

  const send = useCallback(async (text: string, opts: SendOptions = {}) => {
    const question = text.trim();
    if (!question || abortRef.current) return;
    const answerId = localId('a');
    setMessages((prev) => [
      ...prev,
      { localId: localId('u'), role: 'user', content: question, tools: [], sources: [], proposals: [], status: 'done' },
      { localId: answerId, role: 'assistant', content: '', tools: [], sources: [], proposals: [], status: 'streaming', question },
    ]);
    const controller = new AbortController();
    abortRef.current = controller;
    setStreaming(true);
    try {
      await copilotApi.stream(
        { conversationId: conversationRef.current, message: question, patientId: opts.patientId ?? null, locale: opts.locale },
        {
          onTool: (tool) => patch(answerId, (m) => ({ ...m, tools: [...m.tools, tool] })),
          onDelta: (t) => patch(answerId, (m) => ({ ...m, content: m.content + t })),
          onSources: (items) => patch(answerId, (m) => ({ ...m, sources: [...m.sources, ...items] })),
          onProposal: (p) => patch(answerId, (m) => ({ ...m, proposals: [...m.proposals, p] })),
          onDone: (d) => {
            if (d.conversationId) {
              conversationRef.current = d.conversationId;
              setConversationId(d.conversationId);
            }
            patch(answerId, (m) => ({ ...m, status: 'done', messageId: d.messageId }));
          },
          onError: (e) => patch(answerId, (m) => ({ ...m, status: 'error', error: e })),
        },
        controller.signal,
      );
      if (controller.signal.aborted) {
        patch(answerId, (m) => (m.status === 'streaming' ? { ...m, status: 'stopped' } : m));
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setStreaming(false);
    }
  }, [patch]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
    setMessages([]);
    setConversationId(null);
    conversationRef.current = null;
    setNamesHidden(false);
  }, []);

  const applyConversation = useCallback((c: CopilotConversation) => {
    setConversationId(c.id);
    conversationRef.current = c.id;
    setNamesHidden(c.namesAvailable === false);
    setMessages((c.messages ?? []).map((m) => {
      const role = String(m.role).toLowerCase() === 'assistant' ? 'assistant' : 'user';
      const fb = typeof m.feedback === 'string' ? m.feedback.toUpperCase() : null;
      return {
        localId: localId('h'),
        role,
        content: m.content ?? '',
        tools: (m.blocks?.tools ?? []).map(toToolEvent).filter((x): x is CopilotToolEvent => !!x),
        sources: m.blocks?.sources ?? [],
        proposals: (m.blocks?.proposals ?? []).map(toProposal).filter((x): x is CopilotProposal => !!x),
        status: 'done',
        messageId: role === 'assistant' ? m.id : undefined,
        feedback: fb === 'UP' || fb === 'DOWN' ? fb : null,
        fromHistory: true,
      } satisfies CopilotChatMessage;
    }));
  }, []);

  /** Opens a stored conversation. Throws when it cannot be loaded (the caller shows the message). */
  const open = useCallback(async (id: string) => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoadingConversation(true);
    try {
      applyConversation(await copilotApi.getConversation(id));
    } finally {
      setLoadingConversation(false);
    }
  }, [applyConversation]);

  const setFeedback = useCallback((localMessageId: string, feedback: 'UP' | 'DOWN' | null) => {
    patch(localMessageId, (m) => ({ ...m, feedback }));
  }, [patch]);

  return {
    messages, conversationId, streaming, namesHidden, loadingConversation,
    send, stop, reset, open, setFeedback,
  };
}
